import Image from 'next/image';
import React, { createRef, useState, useMemo } from 'react';
import { Trash, Upload } from 'react-feather';
import { v4 as uuid } from 'uuid';

import { Button, Confirm, DashSpinner, Input, Modal, Typography } from '@/components';
import {
  useAuthUser,
  useBrandArchive,
  useBrandsSearchExternal,
  useBrandUpdate,
  useSingleCloudinaryUpload
} from '@/hooks';
import { useTranslation } from 'react-i18next';
import { TranslatedLink } from '@/utils/translatedComponents';

import styles from './TeamBrand.module.css';

interface TeamBrandProps {
  setBrands?: React.Dispatch<React.SetStateAction<Api.Brand[]>>;
  handleRemove?: TypeAny;
  isSearch?: boolean;
  brand: Api.Brand;
}

export function TeamBrand({ isSearch = false, handleRemove, setBrands, brand }: TeamBrandProps) {
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [editingBrand, setEditingBrand] = useState(false);
  const [imageRaw, setImageRaw] = useState('' as TypeAny);
  const [image, setImage] = useState('' as TypeAny);
  const [cloudinaryImageUrl, setCloudinaryImageUrl] = useState('' as TypeAny);
  const [formData, setFormData] = useState({
    name: brand.name,
    description: brand.description,
    url: brand.url,
    logo: undefined as TypeAny,
    image,
    term: ''
  });
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [cloudinaryId, setCloudinaryId] = useState(undefined as TypeAny);
  const [originalBrandState, setOriginalBrandState] = useState<Api.Brand | null>(null);
  const user = useAuthUser();
  const { t } = useTranslation();

  const brandImageRef = createRef<TypeAny>();
  const randomId = useMemo(() => uuid(), []);
  const publicId = useMemo(() => `${brand.brand_id}-${randomId}`, [brand.brand_id, randomId]);

  const { result: cloudinaryResult, uploadFile } = useSingleCloudinaryUpload();

  // Trigger upload when image is ready
  React.useEffect(() => {
    if (uploadedImage && imageRaw && publicId) {
      setIsUploadingImage(true);
      uploadFile(imageRaw, publicId);
    }
  }, [uploadedImage, imageRaw, publicId, uploadFile]);

  // Update cloudinaryId when upload completes
  React.useEffect(() => {
    if (cloudinaryResult && cloudinaryResult.public_id) {
      console.log('🔍 Cloudinary upload complete - Result:', cloudinaryResult);
      console.log('🔍 Cloudinary URL (should be .jpg):', cloudinaryResult.secure_url);
      setCloudinaryId(cloudinaryResult.public_id);
      setCloudinaryImageUrl(cloudinaryResult.secure_url);
      setIsUploadingImage(false);
    }
  }, [cloudinaryResult]);

  const cleanupImage = () => {
    URL.revokeObjectURL(image);
    brandImageRef.current.value = null;
    setImageRaw(null);
    setImage('');
    setCloudinaryImageUrl('');
    setCloudinaryId(undefined);
    setIsUploadingImage(false);
  };

  const { mutate: removeBrand, isLoading: isRemovingBrand } = useBrandArchive({
    onSuccess: () => handleRemove(brand.brand_id),
    id: brand.brand_id
  });

  const { updateBrand, isLoading: isUpdatingBrand } = useBrandUpdate({
    onSuccess: (data: Api.Brand) => {
      if (typeof setBrands === 'function') {
        setBrands((prevState) => [
          ...prevState.map((oldBrand) => {
            if (oldBrand.brand_id !== brand.brand_id) return oldBrand;

            return {
              ...oldBrand,
              ...data
            };
          })
        ]);
      }
      setEditingBrand(false);
      cleanupImage();
    }
  } as TypeAny);

  const {
    mutate: brandsSearchExternal,
    isLoading: isSearching,
    brandsExternal
  } = useBrandsSearchExternal(formData?.term);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      [event.target.name]: event.target.value
    }));
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateBrand({
      ...formData,
      image: uploadedImage,
      id: brand.brand_id,
      cloudinaryId
    });
  };

  const handleSearch = (event: TypeAny) => {
    event.preventDefault();
    setShowSearchResults(true);
    brandsSearchExternal();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSearch(event);
    }
  };

  const handleBrandSelect = (event: TypeAny, brandItem: Api.BrandExternal) => {
    event.preventDefault();
    event.stopPropagation();
    setFormData((prevState) => ({
      ...prevState,
      logo: brandItem.logo,
      term: ''
    }));
    setShowSearchResults(false);

    // Clear custom uploaded image when brandfetch logo is selected
    if (image) {
      cleanupImage();
      setUploadedImage(false);
    }

    // Update the brand in the parent component's brands list immediately
    if (typeof setBrands === 'function') {
      setBrands((prevState) =>
        prevState.map((oldBrand) => {
          if (oldBrand.brand_id !== brand.brand_id) return oldBrand;

          return {
            ...oldBrand,
            logo: brandItem.logo,
            logo_url: brandItem.logo
          };
        })
      );
    }
  };

  const handleBrandSelectTouch = (event: React.TouchEvent, brandItem: Api.BrandExternal) => {
    event.preventDefault();
    event.stopPropagation();
    // Use the same logic as handleBrandSelect but for touch events
    handleBrandSelect(event as TypeAny, brandItem);
  };

  const handleImageUpload = (newImage: Blob | MediaSource) => {
    if (image) cleanupImage();

    const blobUrl = URL.createObjectURL(newImage);
    console.log('🔍 Custom logo upload - Local blob URL:', blobUrl);
    setImage(blobUrl);
    setImageRaw(newImage);
    setUploadedImage(true);

    // Clear brandfetch logo selection when custom image is uploaded
    setFormData((prevState) => ({
      ...prevState,
      logo: undefined
    }));

    // Update the brand in the parent component's brands list to remove brandfetch logo
    if (typeof setBrands === 'function') {
      setBrands((prevState) =>
        prevState.map((oldBrand) => {
          if (oldBrand.brand_id !== brand.brand_id) return oldBrand;

          return {
            ...oldBrand,
            logo: undefined,
            logo_url: undefined
          };
        })
      );
    }
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLFormElement>) => {
    event.preventDefault();
    const newImage = event.target.files[0];

    if (newImage) handleImageUpload(newImage);
  };

  const logoSize = isSearch ? 30 : 60;

  const handleModalOpen = () => {
    // Store the current brand state when opening the modal
    setOriginalBrandState(brand);
    setEditingBrand(true);
  };

  const handleModalClose = () => {
    // Revert all temporary changes when closing modal without saving
    cleanupImage();
    setUploadedImage(false);
    setIsUploadingImage(false);

    // Reset formData to original brand values
    setFormData({
      name: brand.name,
      description: brand.description,
      url: brand.url,
      logo: undefined,
      image: '',
      term: ''
    });

    // Clear Cloudinary URL
    setCloudinaryImageUrl('');

    // Revert parent brands list to the original brand state
    if (typeof setBrands === 'function' && originalBrandState) {
      setBrands((prevState) =>
        prevState.map((oldBrand) => {
          if (oldBrand.brand_id !== brand.brand_id) return oldBrand;

          // Return the original brand state that was stored when modal opened
          return originalBrandState;
        })
      );
    }

    setEditingBrand(false);
  };

  return (
    <>
      {removeConfirm && (
        <Confirm
          handleCancel={() => setRemoveConfirm(false)}
          handleConfirm={removeBrand}
          disabled={isRemovingBrand}
        >
          {t('teamBrand.confirmRemove')}
        </Confirm>
      )}

      {editingBrand && (
        <Modal handleClose={handleModalClose}>
          <div className={styles.teamBrandModal}>
            <div className={styles.teamBrandModalHeading}>
              <div className={styles.teamBrandModalUpload}>
                {isUploadingImage ? (
                  <div
                    className={styles.teamBrandImageSrc}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: 60,
                      width: 60
                    }}
                  >
                    <DashSpinner size={24} color="var(--color-gray)" />
                  </div>
                ) : (
                  <Image
                    src={
                      cloudinaryImageUrl ||
                      image ||
                      formData?.logo ||
                      brand.logo ||
                      brand.logo_square_url
                    }
                    className={styles.teamBrandImageSrc}
                    data-type="upload"
                    alt={brand.name}
                    height={60}
                    width={60}
                  />
                )}

                <Upload className={styles.teamBrandModalImageIcon} size={24} />

                <Input
                  onChange={handleImageChange}
                  ref={brandImageRef}
                  accept="image/*"
                  name="logo"
                  type="file"
                />
              </div>

              <form className={styles.teamBrandModalSearch} onSubmit={handleSearch}>
                <div className={styles.teamBrandsInputsSearch}>
                  <Input
                    placeholder={t('teamBrand.modalLogo')}
                    disabled={isUpdatingBrand}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    name="term"
                  />

                  {!isSearching &&
                    !isUpdatingBrand &&
                    showSearchResults &&
                    brandsExternal?.length > 0 && (
                      <div className={styles.teamBrandsInputsSearchResults}>
                        {brandsExternal?.map((brandItem: TypeAny) => (
                          <button
                            onClick={(event: TypeAny) => handleBrandSelect(event, brandItem)}
                            onTouchEnd={(event: React.TouchEvent) =>
                              handleBrandSelectTouch(event, brandItem)
                            }
                            className={styles.teamBrandsInputsSearchResultsButton}
                            key={`brand-${brandItem.name}-${brandItem.domain}`}
                            type="button"
                          >
                            <TeamBrand brand={brandItem} isSearch />
                          </button>
                        ))}
                      </div>
                    )}
                </div>

                <Button
                  disabled={!formData?.term || isUpdatingBrand}
                  isLoading={isSearching}
                  type="submit"
                  theme="cta"
                >
                  {t('teamBrands.searchCta')}
                </Button>
              </form>
            </div>

            <form className={styles.teamBrandModalForm} onSubmit={handleSubmit}>
              <Input
                label={t('teamBrand.name')}
                disabled={isUpdatingBrand}
                onChange={handleChange}
                value={formData.name}
                name="name"
                required
              />

              <Input
                label={t('teamBrand.description')}
                value={formData.description}
                disabled={isUpdatingBrand}
                onChange={handleChange}
                name="description"
              />

              <Input
                label={t('teamBrand.url')}
                disabled={isUpdatingBrand}
                onChange={handleChange}
                value={formData.url}
                name="url"
              />

              <Button isLoading={isUpdatingBrand} type="submit" theme="cta">
                {t('teamBrand.save')}
              </Button>
            </form>
          </div>
        </Modal>
      )}

      <div className={styles.teamBrand} data-is-search={isSearch}>
        <div className={styles.teamBrandWrapper}>
          <div className={styles.teamBrandImage}>
            {isUploadingImage ? (
              <div
                className={styles.teamBrandImageSrc}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: logoSize,
                  width: logoSize
                }}
              >
                <DashSpinner size={Math.max(16, logoSize * 0.4)} color="var(--color-gray)" />
              </div>
            ) : (
              <Image
                src={cloudinaryImageUrl || image || brand.logo || brand.logo_square_url}
                className={styles.teamBrandImageSrc}
                height={logoSize}
                width={logoSize}
                alt={brand.name}
              />
            )}
          </div>

          <div>
            <div>
              <Typography sizes={[isSearch ? 'body' : 'h5']}>{brand.name}</Typography>
            </div>

            {brand.description && !isSearch && (
              <span className={styles.teamBrandDescription}>{brand.description}</span>
            )}

            {(brand.domain || brand.url) && !isSearch && (
              <TranslatedLink
                href={brand.domain ? `https://${brand.domain}` : brand.url}
                target="_blank"
              >
                {((brand.domain || brand.url) as TypeAny).replace('https://', '')}
              </TranslatedLink>
            )}
          </div>
        </div>

        {user.permissions?.can_edit_brands && !isSearch && (
          <div className={styles.teamBrandPermissions}>
            {brand.brand_id && (
              <button
                onClick={() => setRemoveConfirm(true)}
                className={styles.teamBrandRemove}
                type="button"
              >
                <Trash size={16} />
              </button>
            )}

            <Button onClick={handleModalOpen} size="small">
              {t('teamBrand.edit')}
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
